import { useState, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus, MoreVertical, Play, Power, Bot, Clock, Trash2 } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import {
  Field, FieldDescription, FieldGroup, FieldLabel,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { SearchableSelect } from '@/components/SearchableSelect'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  NONE, extractErr, RecordDrawer, useDeleteConfirm,
} from '@/components/ui/inline-edit'

// ─────────────────── Types ───────────────────
type AgentTool = {
  id: number
  code: string
  label: string
  category: string
  description: string
  risk_level: 'SAFE' | 'WRITE' | 'EXTERNAL'
  is_enabled: boolean
}

type AgentDefinition = {
  id: number
  name: string
  description: string
  system_prompt: string
  role: number | null
  user: number | null
  has_user: boolean
  allowed_tools: number[]
  allowed_tool_codes: string[]
  llm_provider: string
  llm_model: string
  monthly_budget_usd: string
  monthly_spend_usd: string
  approval_policy: 'ALL_AUTONOMOUS' | 'EXTERNAL_OUTREACH_REQUIRES_APPROVAL' | 'EVERY_WRITE_REQUIRES_APPROVAL'
  cadence_recipient_min_days: number
  is_active: boolean
  is_system: boolean
  created_at: string
  updated_at: string
}

type AgentRun = {
  id: number
  agent: number
  status: string
  cost_usd: string
  created_at: string
}

const RISK_COLOR: Record<AgentTool['risk_level'], string> = {
  SAFE: 'bg-green-100 text-green-700',
  WRITE: 'bg-amber-100 text-amber-800',
  EXTERNAL: 'bg-red-100 text-red-700',
}

const POLICY_LABEL: Record<AgentDefinition['approval_policy'], string> = {
  ALL_AUTONOMOUS: 'Fully autonomous',
  EXTERNAL_OUTREACH_REQUIRES_APPROVAL: 'Approve external outreach',
  EVERY_WRITE_REQUIRES_APPROVAL: 'Approve every write',
}

// ─────────────────── Page ───────────────────

export default function AdminAIAgents() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { confirm, dialog } = useDeleteConfirm()

  const agentsQ = useQuery({
    queryKey: ['ai-agents'],
    queryFn: async () => (await api.get<AgentDefinition[]>('/ai-agents/')).data,
  })
  const toolsQ = useQuery({
    queryKey: ['ai-agents', 'tools'],
    queryFn: async () => (await api.get<AgentTool[]>('/ai-agents/tools/')).data,
  })

  const agents = agentsQ.data || []
  const tools = toolsQ.data || []

  const [selectedId, setSelectedId] = useState<number | null>(null)
  const selected = selectedId ? agents.find((a) => a.id === selectedId) || null : null

  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState({ name: '', description: '' })

  const createMut = useMutation({
    mutationFn: async () => {
      if (!draft.name.trim()) throw new Error('Name is required')
      return (await api.post<AgentDefinition>('/ai-agents/', {
        name: draft.name.trim(),
        description: draft.description,
        system_prompt: `You are ${draft.name.trim()}. Edit this prompt to give the agent its persona, goals, and constraints.`,
        approval_policy: 'EXTERNAL_OUTREACH_REQUIRES_APPROVAL',
        monthly_budget_usd: '50.00',
        cadence_recipient_min_days: 14,
        is_active: false,
      })).data
    },
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['ai-agents'] })
      toast.success('Agent created — attach tools + activate to use')
      setCreating(false); setDraft({ name: '', description: '' })
      setSelectedId(created.id)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const updateMut = useMutation({
    mutationFn: async (payload: Partial<AgentDefinition> & { id: number }) =>
      (await api.put<AgentDefinition>(`/ai-agents/${payload.id}/`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ai-agents'] })
      toast.success('Saved')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const runMut = useMutation({
    mutationFn: async ({ id, prompt }: { id: number; prompt: string }) =>
      (await api.post<AgentRun>(`/ai-agents/${id}/run/`, { prompt })).data,
    onSuccess: (run) => {
      qc.invalidateQueries({ queryKey: ['ai-agents'] })
      toast.success(`Run #${run.id} queued — see Runs tab`)
      navigate(`/admin/ai-agents/runs/${run.id}`)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const killMut = useMutation({
    mutationFn: async (id: number) =>
      (await api.post(`/ai-agents/${id}/kill/`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ai-agents'] })
      toast.success('Agent killed — is_active=false')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/ai-agents/${id}/`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ai-agents'] })
      setSelectedId(null)
      toast.success('Agent deleted')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  return (
    <AdminLayout>
      {dialog}

      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Bot className="h-5 w-5 text-muted-foreground" /> AI Agents
          </h2>
          <p className="text-sm text-muted-foreground">
            Virtual agents that act on internal data + (later) external services. Risky actions land in the approval queue.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate('/admin/ai-agents/runs')}>
            All runs
          </Button>
          <Button variant="outline" onClick={() => navigate('/admin/ai-agents/approvals')}>
            Approval queue
          </Button>
          <Button onClick={() => setCreating(true)}>
            <Plus className="h-3 w-3 mr-1" /> New agent
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
        {/* Sidebar: agent list */}
        <aside className="bg-card rounded-lg shadow overflow-hidden self-start">
          {agentsQ.isLoading ? (
            <p className="text-muted-foreground p-4 text-sm">Loading…</p>
          ) : agents.length === 0 ? (
            <p className="text-muted-foreground p-4 text-sm">No agents yet.</p>
          ) : (
            <ul className="divide-y divide-border">
              {agents.map((a) => (
                <li key={a.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(a.id)}
                    className={`w-full text-left px-4 py-3 hover:bg-muted/40 ${
                      selectedId === a.id ? 'bg-violet-50/60' : ''
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`inline-block w-2 h-2 rounded-full ${
                        a.is_active ? 'bg-green-500' : 'bg-muted-foreground'
                      }`} />
                      <span className="text-sm font-medium truncate">{a.name}</span>
                      {a.is_system && (
                        <span className="text-xs px-1 py-0.5 rounded bg-gray-100 text-gray-600">sys</span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {a.allowed_tool_codes.length} tool{a.allowed_tool_codes.length === 1 ? '' : 's'} ·
                      ${a.monthly_spend_usd} / ${a.monthly_budget_usd}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        {/* Right: detail editor */}
        <div className="min-w-0">
          {!selected ? (
            <div className="bg-card rounded-lg shadow p-8 text-center text-muted-foreground text-sm">
              Pick an agent from the left to edit.
            </div>
          ) : (
            <AgentDetail
              key={selected.id}
              agent={selected}
              tools={tools}
              onSave={(payload) => updateMut.mutate({ id: selected.id, ...payload } as any)}
              onRun={(prompt) => runMut.mutate({ id: selected.id, prompt })}
              onKill={() => killMut.mutate(selected.id)}
              onDelete={() => confirm(
                `agent "${selected.name}"`,
                () => deleteMut.mutate(selected.id),
              )}
              saving={updateMut.isPending}
              running={runMut.isPending}
            />
          )}
        </div>
      </div>

      <RecordDrawer
        open={creating}
        onOpenChange={(v) => { if (!v) setCreating(false) }}
        title="New AI agent"
        description="Create the agent shell. Attach tools, edit the system prompt, set the budget, and activate it on the next page."
        onSubmit={() => createMut.mutate()}
        isPending={createMut.isPending}
        submitLabel="Create"
      >
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="ag-name">Name *</FieldLabel>
            <Input id="ag-name" autoFocus value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder='e.g. "Recruitment Officer AI"' />
          </Field>
          <Field>
            <FieldLabel htmlFor="ag-desc">Description</FieldLabel>
            <Textarea id="ag-desc" rows={3} value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              placeholder="One-line summary of what this agent does." />
          </Field>
        </FieldGroup>
      </RecordDrawer>
    </AdminLayout>
  )
}

// ─────────────────── Detail editor ───────────────────

function AgentDetail({
  agent, tools, onSave, onRun, onKill, onDelete, saving, running,
}: {
  agent: AgentDefinition
  tools: AgentTool[]
  onSave: (payload: Partial<AgentDefinition>) => void
  onRun: (prompt: string) => void
  onKill: () => void
  onDelete: () => void
  saving: boolean
  running: boolean
}) {
  const [name, setName] = useState(agent.name)
  const [description, setDescription] = useState(agent.description)
  const [prompt, setPrompt] = useState(agent.system_prompt)
  const [budget, setBudget] = useState(agent.monthly_budget_usd)
  const [policy, setPolicy] = useState(agent.approval_policy)
  const [cadenceDays, setCadenceDays] = useState(agent.cadence_recipient_min_days)
  const [isActive, setIsActive] = useState(agent.is_active)
  const [selectedToolIds, setSelectedToolIds] = useState<number[]>(agent.allowed_tools || [])
  // Component re-mounts via `key` on parent when the agent selection changes,
  // so all the useState initializers above re-evaluate for the new agent.

  const [runPrompt, setRunPrompt] = useState('')

  function save() {
    onSave({
      name, description, system_prompt: prompt,
      monthly_budget_usd: budget,
      approval_policy: policy,
      cadence_recipient_min_days: cadenceDays,
      is_active: isActive,
      allowed_tools: selectedToolIds,
    })
  }

  function toggleTool(id: number) {
    setSelectedToolIds((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id])
  }

  const toolsByCategory = new Map<string, AgentTool[]>()
  for (const t of tools) {
    const c = t.category || 'Other'
    if (!toolsByCategory.has(c)) toolsByCategory.set(c, [])
    toolsByCategory.get(c)!.push(t)
  }

  return (
    <div className="space-y-4">
      {/* Header strip */}
      <div className="bg-card rounded-lg shadow p-4 flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-mono text-xs text-muted-foreground">Agent #{agent.id}</div>
          <h3 className="text-lg font-bold truncate">{agent.name}</h3>
          <div className="text-xs text-muted-foreground">
            ${agent.monthly_spend_usd} / ${agent.monthly_budget_usd} this month ·
            {' '}{POLICY_LABEL[agent.approval_policy]} ·
            {' '}<span className={agent.is_active ? 'text-green-700' : 'text-muted-foreground'}>
              {agent.is_active ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="p-1.5 rounded hover:bg-muted text-muted-foreground">
                <MoreVertical className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {agent.is_active && (
                <DropdownMenuItem onClick={onKill} className="text-destructive">
                  <Power className="h-3.5 w-3.5 mr-2" /> Kill (deactivate)
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                disabled={agent.is_system}
                onClick={() => !agent.is_system && onDelete()}
                className={!agent.is_system ? 'text-destructive' : ''}
              >
                {agent.is_system ? 'Delete (system agent)' : 'Delete agent'}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Run-now strip — only when active */}
      <div className="bg-card rounded-lg shadow p-4">
        <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
          Run now
        </div>
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <Textarea
              rows={2} value={runPrompt}
              onChange={(e) => setRunPrompt(e.target.value)}
              placeholder='e.g. "Find an open Senior Engineer role and draft an outreach message to a candidate that fits."'
            />
          </div>
          <Button
            onClick={() => onRun(runPrompt.trim() || 'Begin your task.')}
            disabled={running || !agent.is_active}
            title={!agent.is_active ? 'Activate the agent first' : 'Start a run'}
          >
            <Play className="h-3 w-3 mr-1" /> {running ? 'Starting…' : 'Run'}
          </Button>
        </div>
      </div>

      {/* Editable config */}
      <div className="bg-card rounded-lg shadow p-4">
        <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
          Configuration
        </h4>
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="d-name">Name</FieldLabel>
            <Input id="d-name" value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field>
            <FieldLabel htmlFor="d-desc">Description</FieldLabel>
            <Textarea id="d-desc" rows={2} value={description}
              onChange={(e) => setDescription(e.target.value)} />
          </Field>
          <Field>
            <FieldLabel htmlFor="d-prompt">System prompt</FieldLabel>
            <Textarea id="d-prompt" rows={10} value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              className="font-mono text-sm"
            />
            <FieldDescription>
              The agent's persona, goals, and hard constraints. Sent on every LLM call as the system role.
            </FieldDescription>
          </Field>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field>
              <FieldLabel htmlFor="d-budget">Monthly budget (USD)</FieldLabel>
              <Input id="d-budget" type="number" step="0.01" min="0" value={budget}
                onChange={(e) => setBudget(e.target.value)} />
              <FieldDescription>Spent: ${agent.monthly_spend_usd}</FieldDescription>
            </Field>
            <Field>
              <FieldLabel htmlFor="d-policy">Approval policy</FieldLabel>
              <SearchableSelect
                id="d-policy" value={policy}
                onChange={(v) => setPolicy(v as AgentDefinition['approval_policy'])}
                placeholder="—"
                options={[
                  { value: 'EVERY_WRITE_REQUIRES_APPROVAL', label: 'Approve every write (safest)' },
                  { value: 'EXTERNAL_OUTREACH_REQUIRES_APPROVAL', label: 'Approve external outreach (default)' },
                  { value: 'ALL_AUTONOMOUS', label: 'Fully autonomous (external still requires approval)' },
                ]}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="d-cad">Cadence cap (days)</FieldLabel>
              <Input id="d-cad" type="number" min="0" value={cadenceDays}
                onChange={(e) => setCadenceDays(Number(e.target.value) || 0)} />
              <FieldDescription>Min days between messages to the same recipient.</FieldDescription>
            </Field>
          </div>
          <Field>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)} />
              <strong>Active</strong>
            </label>
            <FieldDescription>Inactive agents cannot start new runs.</FieldDescription>
          </Field>
        </FieldGroup>
      </div>

      {/* Schedules */}
      <AgentSchedules agentId={agent.id} />

      {/* Allowed tools */}
      <div className="bg-card rounded-lg shadow p-4">
        <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
          Allowed tools ({selectedToolIds.length} of {tools.length})
        </h4>
        <div className="space-y-4">
          {Array.from(toolsByCategory.keys()).sort().map((cat) => (
            <div key={cat}>
              <div className="text-xs font-semibold text-muted-foreground mb-2">{cat}</div>
              <div className="space-y-1.5">
                {toolsByCategory.get(cat)!.map((t) => (
                  <label key={t.id} className="flex items-start gap-2 cursor-pointer p-1.5 rounded hover:bg-muted/40">
                    <input
                      type="checkbox"
                      checked={selectedToolIds.includes(t.id)}
                      onChange={() => toggleTool(t.id)}
                      disabled={!t.is_enabled}
                      className="mt-1"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm">{t.code}</span>
                        <span className={`text-xs px-1.5 py-0.5 rounded font-semibold ${RISK_COLOR[t.risk_level]}`}>
                          {t.risk_level}
                        </span>
                        {!t.is_enabled && (
                          <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">disabled globally</span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground line-clamp-2">{t.description}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ─────────────────── Agent schedules sub-component ───────────────────

type Schedule = {
  id: number
  agent: number
  agent_name: string
  label: string
  cron_expression: string
  input_context: Record<string, any>
  is_active: boolean
  last_run_at: string | null
  last_run_id: number | null
  next_run_at: string | null
  created_at: string
  updated_at: string
}

const CRON_EXAMPLES = [
  { expr: '0 9 * * 1-5', meaning: 'Weekdays at 09:00' },
  { expr: '0 8 * * 1',   meaning: 'Every Monday at 08:00' },
  { expr: '*/30 * * * *', meaning: 'Every 30 minutes' },
  { expr: '0 * * * *',   meaning: 'Top of every hour' },
  { expr: '0 0 * * *',   meaning: 'Daily at midnight UTC' },
]

function AgentSchedules({ agentId }: { agentId: number }) {
  const qc = useQueryClient()
  const schedulesQ = useQuery({
    queryKey: ['ai-agents', 'schedules', agentId],
    queryFn: async () =>
      (await api.get<Schedule[]>(`/ai-agents/schedules/?agent=${agentId}`)).data,
  })

  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState({
    label: '', cron_expression: '', prompt: '', is_active: true,
  })

  const createMut = useMutation({
    mutationFn: async () => {
      if (!draft.cron_expression.trim()) throw new Error('Cron expression is required')
      return (await api.post<Schedule>('/ai-agents/schedules/', {
        agent: agentId,
        label: draft.label.trim(),
        cron_expression: draft.cron_expression.trim(),
        input_context: draft.prompt.trim() ? { prompt: draft.prompt.trim() } : {},
        is_active: draft.is_active,
      })).data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ai-agents', 'schedules', agentId] })
      toast.success('Schedule created')
      setAdding(false)
      setDraft({ label: '', cron_expression: '', prompt: '', is_active: true })
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const toggleMut = useMutation({
    mutationFn: async ({ id, value }: { id: number; value: boolean }) =>
      (await api.patch<Schedule>(`/ai-agents/schedules/${id}/`, { is_active: value })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ai-agents', 'schedules', agentId] }),
    onError: (e) => toast.error(extractErr(e)),
  })

  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/ai-agents/schedules/${id}/`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ai-agents', 'schedules', agentId] })
      toast.success('Schedule deleted')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const schedules = schedulesQ.data || []

  return (
    <div className="bg-card rounded-lg shadow p-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1">
          <Clock className="h-3.5 w-3.5" /> Schedules
        </h4>
        <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
          <Plus className="h-3 w-3 mr-1" /> Add
        </Button>
      </div>

      {schedulesQ.isLoading ? (
        <p className="text-xs text-muted-foreground">Loading…</p>
      ) : schedules.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          No schedules. Add one to have the agent run automatically (e.g. every weekday at 09:00).
        </p>
      ) : (
        <ul className="divide-y divide-border">
          {schedules.map((s) => (
            <li key={s.id} className="py-2 flex items-center gap-3">
              <input type="checkbox" checked={s.is_active}
                onChange={(e) => toggleMut.mutate({ id: s.id, value: e.target.checked })} />
              <div className="flex-1 min-w-0">
                <div className="text-sm">
                  {s.label && <span className="font-medium">{s.label} · </span>}
                  <code className="font-mono text-xs">{s.cron_expression}</code>
                </div>
                <div className="text-xs text-muted-foreground">
                  Next run: {s.next_run_at ? new Date(s.next_run_at).toLocaleString() : '—'}
                  {s.last_run_at && (
                    <> · Last: {new Date(s.last_run_at).toLocaleString()}</>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => deleteMut.mutate(s.id)}
                className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-destructive"
                title="Delete schedule"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {adding && (
        <div className="mt-3 border-t border-border pt-3 space-y-2">
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="sch-label">Label</FieldLabel>
              <Input id="sch-label" value={draft.label}
                onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                placeholder='e.g. "Morning prospecting digest"' />
            </Field>
            <Field>
              <FieldLabel htmlFor="sch-cron">Cron expression *</FieldLabel>
              <Input id="sch-cron" value={draft.cron_expression}
                onChange={(e) => setDraft({ ...draft, cron_expression: e.target.value })}
                placeholder="0 9 * * 1-5"
                className="font-mono" />
              <FieldDescription>
                Standard 5-field cron (minute hour day month weekday). Examples:
                <ul className="mt-1 ml-3 list-disc text-xs">
                  {CRON_EXAMPLES.map((ex) => (
                    <li key={ex.expr}>
                      <code className="font-mono text-violet-700">{ex.expr}</code> — {ex.meaning}
                    </li>
                  ))}
                </ul>
              </FieldDescription>
            </Field>
            <Field>
              <FieldLabel htmlFor="sch-prompt">Prompt for each run (optional)</FieldLabel>
              <Textarea id="sch-prompt" rows={2} value={draft.prompt}
                onChange={(e) => setDraft({ ...draft, prompt: e.target.value })}
                placeholder='e.g. "Find candidates added in the last 24h and triage them"' />
              <FieldDescription>
                Passed as the agent's first user message on every scheduled run.
              </FieldDescription>
            </Field>
            <Field>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={draft.is_active}
                  onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })} />
                Activate immediately
              </label>
            </Field>
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="outline" onClick={() => setAdding(false)}>Cancel</Button>
              <Button size="sm" onClick={() => createMut.mutate()} disabled={createMut.isPending}>
                {createMut.isPending ? 'Adding…' : 'Add schedule'}
              </Button>
            </div>
          </FieldGroup>
        </div>
      )}
    </div>
  )
}
